import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  CSSObject,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  alpha,
  getContrastRatio,
  styled,
  useTheme,
} from '@mui/material';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Visual } from './cardTypes';
import { downloadCsv, toCsv } from './csv';
import {
  formatCompactNumber,
  formatValue,
  isNumericFormat,
} from './formatValue';

interface Column {
  field: string;
  label: string;
  format?: string;
}

interface Series {
  field: string;
  label: string;
}

type Row = Record<string, unknown>;

interface KeyedRow {
  key: string;
  position: number;
  row: Row;
}

interface Shade {
  main: string;
  light: string;
  dark: string;
}

type ChartKind = NonNullable<Visual['chart']>;
type SortState = { field: string; direction: 'asc' | 'desc' } | null;

export interface AlphaVisualProps {
  visual: Visual;
  compact?: boolean;
}

const PAGE_SIZE = 20;
const CHART_HEIGHT = 260;
const COMPACT_CHART_HEIGHT = 200;
const MIN_MARK_CONTRAST = 3;
const MUTED_TEXT = 0.72;
const DOT_LIMIT = 24;
const EMPTY_CELL = '—';
const PRINT_ONLY_CLASS = 'alpha-visual-print-only';
const TABLE_HINT = 'Use Show as table for the values.';
const CHART_MARGIN = { top: 8, right: 24, bottom: 0, left: 0 };

const CHART_NAMES: Record<ChartKind, string> = {
  bar: 'Bar chart',
  stacked_bar: 'Stacked bar chart',
  line: 'Line chart',
  area: 'Area chart',
  pie: 'Pie chart',
};

const muted = (theme: Theme): string =>
  alpha(theme.palette.text.primary, MUTED_TEXT);

const focusRing = (theme: Theme): CSSObject => ({
  '&:focus-visible, &.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.text.primary}`,
    outlineOffset: 2,
  },
});

const printOnly = (display: 'block' | 'table-row'): CSSObject => ({
  [`&.${PRINT_ONLY_CLASS}`]: { display: 'none' },
  '@media print': { [`&.${PRINT_ONLY_CLASS}`]: { display } },
});

const Root = styled('div', {
  shouldForwardProp: prop => prop !== 'compact',
})<{ compact: boolean }>(({ theme, compact }) => ({
  isolation: 'isolate',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  minWidth: 0,
  padding: theme.spacing(compact ? 1 : 1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Title = styled('p')(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  margin: 0,
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 600,
  lineHeight: 1.4,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
}));

const Note = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.5,
  color: muted(theme),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  minHeight: 32,
  padding: theme.spacing(0.5, 1.25),
  border: `1px solid ${alpha(theme.palette.text.primary, 0.24)}`,
  borderRadius: 6,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'none',
  color: theme.palette.text.primary,
  transition: 'background-color 150ms ease, border-color 150ms ease',
  '&:hover': {
    background: alpha(theme.palette.text.primary, 0.06),
    borderColor: alpha(theme.palette.text.primary, 0.4),
  },
  '&:active': { background: alpha(theme.palette.text.primary, 0.12) },
  '& .MuiButton-startIcon': { marginRight: theme.spacing(0.5) },
  ...focusRing(theme),
}));

const ToggleButton = styled(ActionButton)({ marginLeft: 'auto' });

const ShowAllButton = styled(ActionButton)({
  marginRight: 'auto',
  '@media print': { display: 'none' },
});

const PrintNote = styled(Note)({
  alignSelf: 'center',
  marginRight: 'auto',
  ...printOnly('block'),
});

const Footer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));

const FrameWrap = styled('div', {
  shouldForwardProp: prop => prop !== 'compact',
})<{ compact: boolean }>(({ theme, compact }) => ({
  position: 'relative',
  isolation: 'isolate',
  minWidth: 0,
  margin: theme.spacing(0, compact ? -1 : -1.5),
}));

const TableFrame = styled(TableContainer, {
  shouldForwardProp: prop => prop !== 'compact',
})<{ compact: boolean }>(({ theme, compact }) => ({
  maxHeight: compact ? 320 : 440,
  width: 'auto',
  '& .MuiTableCell-root': {
    padding: theme.spacing(0.75, compact ? 1 : 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '@media print': { maxHeight: 'none', overflow: 'visible' },
  ...focusRing(theme),
}));

interface ScrollbarGutter {
  right: number;
  bottom: number;
}

const EdgeFade = styled('div', {
  shouldForwardProp: prop => prop !== 'gutter',
})<{ gutter: ScrollbarGutter }>(({ theme, gutter }) => ({
  position: 'absolute',
  top: 0,
  right: gutter.right,
  bottom: gutter.bottom,
  zIndex: 3,
  width: theme.spacing(4),
  pointerEvents: 'none',
  background: `linear-gradient(to right, ${alpha(
    theme.palette.background.paper,
    0,
  )}, ${theme.palette.background.paper})`,
  '@media print': { display: 'none' },
}));

const ScrollCue = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.5,
  color: muted(theme),
  '@media print': { display: 'none' },
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  color: muted(theme),
  background: theme.palette.background.paper,
}));

const SortLabel = styled(TableSortLabel)(({ theme }) => ({
  borderRadius: 4,
  color: 'inherit',
  '&:hover, &.Mui-active': { color: theme.palette.text.primary },
  '&.Mui-active .MuiTableSortLabel-icon': { color: 'inherit' },
  ...focusRing(theme),
}));

const BodyRow = styled(TableRow)(({ theme }) => ({
  '&:hover': { background: alpha(theme.palette.text.primary, 0.04) },
  '&:last-of-type .MuiTableCell-root': { borderBottom: 0 },
  '@media screen': {
    [`&:has(+ .${PRINT_ONLY_CLASS}) .MuiTableCell-root`]: { borderBottom: 0 },
  },
  ...printOnly('table-row'),
}));

const BodyCell = styled(TableCell)(({ theme }) => ({
  maxWidth: 360,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
  '&.MuiTableCell-alignLeft': { minWidth: '12ch' },
  '&.MuiTableCell-alignRight': {
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
}));

const ChartFrame = styled('div')(({ theme }) => ({
  width: '100%',
  minWidth: 0,
  fontFamily: theme.typography.fontFamily,
}));

const LegendLabel = styled('span')(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  color: muted(theme),
}));

const humanize = (field: string): string => {
  const words = field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.\s]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const alignFor = (column: Column): 'left' | 'right' =>
  isNumericFormat(column.format) ? 'right' : 'left';

const csvFileName = (title?: string): string =>
  `${(title ?? '').trim() || 'alpha-table'}.csv`;

const keyRows = (rows: Row[]): KeyedRow[] =>
  rows.map((row, position) => ({ key: String(position), position, row }));

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || value === '';

const compareValues = (left: unknown, right: unknown): number =>
  typeof left === 'number' && typeof right === 'number'
    ? left - right
    : String(left).localeCompare(String(right), 'en-US', {
        numeric: true,
        sensitivity: 'base',
      });

const sortRows = (rows: KeyedRow[], sort: SortState): KeyedRow[] => {
  if (sort === null) {
    return rows;
  }
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = a.row[sort.field];
    const right = b.row[sort.field];
    if (isBlank(left) || isBlank(right)) {
      return Number(isBlank(left)) - Number(isBlank(right));
    }
    return compareValues(left, right) * factor;
  });
};

const nextSort = (current: SortState, field: string): SortState => {
  if (current === null || current.field !== field) {
    return { field, direction: 'asc' };
  }
  return current.direction === 'asc' ? { field, direction: 'desc' } : null;
};

const seriesColours = (theme: Theme): string[] => {
  const { primary, secondary, warning, info, success, background, text } =
    theme.palette;
  const stronger = (shade: Shade): string[] => [
    shade.main,
    theme.palette.mode === 'dark' ? shade.light : shade.dark,
  ];
  const picked = [
    [primary.main],
    stronger(warning),
    stronger(info),
    stronger(success),
    [secondary.main],
  ]
    .map(shades =>
      shades.find(
        shade => getContrastRatio(shade, background.paper) >= MIN_MARK_CONTRAST,
      ),
    )
    .filter((shade): shade is string => shade !== undefined);
  const distinct = picked.filter(
    (shade, index) => picked.indexOf(shade) === index,
  );
  const solid = distinct.length > 0 ? distinct : [text.primary];
  return [...solid, ...solid.map(shade => alpha(shade, 0.55))];
};

const colourAt = (colours: string[], index: number): string =>
  colours[index % colours.length];

const chartColumns = (visual: Visual): Column[] => [
  ...(visual.x
    ? [{ field: visual.x, label: humanize(visual.x), format: visual.xFormat }]
    : []),
  ...(visual.series ?? []).map(entry => ({
    field: entry.field,
    label: entry.label,
    format: 'number',
  })),
];

const chartLabel = (
  kind: ChartKind,
  title: string | undefined,
  series: Series[],
  x: string,
): string => {
  const subject =
    title === undefined
      ? CHART_NAMES[kind]
      : `${CHART_NAMES[kind]} of ${title}`;
  const shown = kind === 'pie' ? series.slice(0, 1) : series;
  const labels = shown.map(entry => entry.label).join(', ');
  const axis = humanize(x);
  return `${subject}: ${labels} by ${axis}. ${TABLE_HINT}`;
};

const columnsFor = (visual: Visual): Column[] => {
  if (visual.kind === 'chart') {
    return chartColumns(visual);
  }
  return visual.columns ?? [];
};

const formatNumber = (value: unknown): string => formatValue(value, 'number');

const renderLegendLabel = (text: string): JSX.Element => (
  <LegendLabel>{text}</LegendLabel>
);

export interface ScrollMetrics {
  scrollWidth: number;
  clientWidth: number;
  scrollLeft: number;
}

export interface HorizontalOverflow {
  overflows: boolean;
  atEnd: boolean;
}

const SCROLL_SLACK = 1;
const SCROLL_CUE = 'Scroll for more columns';

export const horizontalOverflow = ({
  scrollWidth,
  clientWidth,
  scrollLeft,
}: ScrollMetrics): HorizontalOverflow => {
  const overflows = scrollWidth - clientWidth > SCROLL_SLACK;
  return {
    overflows,
    atEnd: !overflows || scrollLeft + clientWidth >= scrollWidth - SCROLL_SLACK,
  };
};

const VisualTable = ({
  title,
  columns,
  rows,
  truncated,
  compact,
}: {
  title?: string;
  columns: Column[];
  rows: Row[];
  truncated: boolean;
  compact: boolean;
}): JSX.Element => {
  const [sort, setSort] = useState<SortState>(null);
  const [showAll, setShowAll] = useState(false);
  const [edge, setEdge] = useState<HorizontalOverflow>({
    overflows: false,
    atEnd: true,
  });
  const [gutter, setGutter] = useState<ScrollbarGutter>({
    right: 0,
    bottom: 0,
  });
  const frameRef = useRef<HTMLDivElement>(null);
  const cueId = useId();
  const keyed = useMemo(() => keyRows(rows), [rows]);
  const sorted = useMemo(() => sortRows(keyed, sort), [keyed, sort]);
  const visible = showAll ? sorted : sorted.slice(0, PAGE_SIZE);

  const measure = useCallback((): void => {
    const node = frameRef.current;
    if (node == null) {
      return;
    }
    const next = horizontalOverflow(node);
    const right = Math.max(node.offsetWidth - node.clientWidth, 0);
    const bottom = Math.max(node.offsetHeight - node.clientHeight, 0);
    setGutter(current =>
      current.right === right && current.bottom === bottom
        ? current
        : { right, bottom },
    );
    setEdge(current =>
      current.overflows === next.overflows && current.atEnd === next.atEnd
        ? current
        : next,
    );
  }, []);

  useEffect(() => {
    measure();
  }, [measure, columns, visible.length]);

  useEffect(() => {
    const node = frameRef.current;
    if (node == null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(node);
    const table = node.querySelector('table');
    if (table != null) {
      observer.observe(table);
    }
    return () => {
      observer.disconnect();
    };
  }, [measure, rows.length]);

  if (rows.length === 0) {
    return <Note>No rows to show.</Note>;
  }

  const collapsed = !showAll && sorted.length > PAGE_SIZE;
  const printNote = !truncated && sorted.length > PAGE_SIZE;
  const showCue = edge.overflows && !edge.atEnd;
  const download = (): void => {
    downloadCsv(
      csvFileName(title),
      toCsv(
        columns,
        sorted.map(entry => entry.row),
      ),
    );
  };

  return (
    <>
      <FrameWrap compact={compact}>
        <TableFrame
          ref={frameRef}
          compact={compact}
          tabIndex={edge.overflows ? 0 : undefined}
          aria-describedby={showCue ? cueId : undefined}
          onScroll={measure}
        >
          <Table stickyHeader size="small" aria-label={title ?? 'Alpha table'}>
            <TableHead>
              <TableRow>
                {columns.map(column => {
                  const direction =
                    sort?.field === column.field ? sort.direction : undefined;
                  return (
                    <HeaderCell
                      key={column.field}
                      align={alignFor(column)}
                      sortDirection={direction ?? false}
                    >
                      <SortLabel
                        active={direction !== undefined}
                        direction={direction ?? 'asc'}
                        onClick={() => {
                          setSort(current => nextSort(current, column.field));
                        }}
                      >
                        {column.label}
                      </SortLabel>
                    </HeaderCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((entry, index) => (
                <BodyRow
                  key={entry.key}
                  className={
                    collapsed && index >= PAGE_SIZE
                      ? PRINT_ONLY_CLASS
                      : undefined
                  }
                >
                  {columns.map(column => (
                    <BodyCell key={column.field} align={alignFor(column)}>
                      {formatValue(entry.row[column.field], column.format) ||
                        EMPTY_CELL}
                    </BodyCell>
                  ))}
                </BodyRow>
              ))}
            </TableBody>
          </Table>
        </TableFrame>
        {showCue ? (
          <EdgeFade aria-hidden="true" data-alpha-scroll-fade gutter={gutter} />
        ) : null}
      </FrameWrap>
      {showCue ? <ScrollCue id={cueId}>{SCROLL_CUE}</ScrollCue> : null}
      <Footer>
        {printNote ? (
          <PrintNote className={PRINT_ONLY_CLASS}>
            {`All ${sorted.length} rows`}
          </PrintNote>
        ) : null}
        {collapsed ? (
          <ShowAllButton
            disableRipple
            onClick={() => {
              setShowAll(true);
            }}
          >
            {`Show all ${sorted.length}`}
          </ShowAllButton>
        ) : null}
        <ActionButton
          disableRipple
          startIcon={<FileDownloadOutlined />}
          onClick={download}
        >
          Download CSV
        </ActionButton>
      </Footer>
    </>
  );
};

const VisualChart = ({
  visual,
  title,
  compact,
}: {
  visual: Visual;
  title?: string;
  compact: boolean;
}): JSX.Element => {
  const theme = useTheme();
  const colours = useMemo(() => seriesColours(theme), [theme]);
  const keyed = useMemo(() => keyRows(visual.rows), [visual.rows]);
  const kind = visual.chart ?? 'bar';
  const series = visual.series ?? [];
  const x = visual.x ?? '';
  const formatX = (value: unknown): string =>
    formatValue(value, visual.xFormat);

  if (visual.rows.length === 0 || series.length === 0) {
    return <Note>No data to chart.</Note>;
  }

  const surface = theme.palette.background.paper;
  const grid = theme.palette.divider;
  const tick = {
    fill: muted(theme),
    fontSize: theme.typography.caption.fontSize,
  };
  const tooltip = {
    contentStyle: {
      background: surface,
      border: `1px solid ${grid}`,
      borderRadius: 6,
      fontSize: theme.typography.caption.fontSize,
      padding: theme.spacing(0.75, 1),
    },
    labelStyle: {
      color: theme.palette.text.primary,
      fontWeight: 600,
      marginBottom: 2,
    },
    itemStyle: { color: theme.palette.text.primary, padding: 0 },
  };
  const legend =
    series.length > 1 || kind === 'pie' ? (
      <Legend
        iconType="circle"
        iconSize={8}
        formatter={value =>
          renderLegendLabel(kind === 'pie' ? formatX(value) : String(value))
        }
      />
    ) : null;
  const axes = (
    <>
      <CartesianGrid vertical={false} stroke={grid} />
      <XAxis
        dataKey={x}
        tick={tick}
        tickLine={false}
        axisLine={{ stroke: grid }}
        minTickGap={16}
        tickFormatter={formatX}
      />
      <YAxis
        tick={tick}
        tickLine={false}
        axisLine={false}
        tickFormatter={formatCompactNumber}
        width={56}
      />
    </>
  );
  const activeDot = { r: 4, stroke: surface, strokeWidth: 2 };
  const showDots = visual.rows.length <= DOT_LIMIT;

  const renderChart = (): JSX.Element => {
    if (kind === 'pie') {
      return (
        <PieChart margin={CHART_MARGIN}>
          <Pie
            data={visual.rows}
            dataKey={series[0].field}
            nameKey={x}
            innerRadius="55%"
            outerRadius="85%"
            stroke={surface}
            strokeWidth={2}
            isAnimationActive={false}
          >
            {keyed.map(entry => (
              <Cell key={entry.key} fill={colourAt(colours, entry.position)} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [formatNumber(value), formatX(name)]}
            {...tooltip}
          />
          {legend}
        </PieChart>
      );
    }
    if (kind === 'line') {
      return (
        <LineChart data={visual.rows} margin={CHART_MARGIN}>
          {axes}
          <Tooltip
            formatter={formatNumber}
            labelFormatter={formatX}
            cursor={{ stroke: grid }}
            {...tooltip}
          />
          {legend}
          {series.map((entry, index) => (
            <Line
              key={entry.field}
              type="monotone"
              dataKey={entry.field}
              name={entry.label}
              stroke={colourAt(colours, index)}
              strokeWidth={2}
              dot={
                showDots
                  ? { r: 3, strokeWidth: 0, fill: colourAt(colours, index) }
                  : false
              }
              activeDot={activeDot}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      );
    }
    if (kind === 'area') {
      return (
        <AreaChart data={visual.rows} margin={CHART_MARGIN}>
          {axes}
          <Tooltip
            formatter={formatNumber}
            labelFormatter={formatX}
            cursor={{ stroke: grid }}
            {...tooltip}
          />
          {legend}
          {series.map((entry, index) => (
            <Area
              key={entry.field}
              type="monotone"
              dataKey={entry.field}
              name={entry.label}
              stroke={colourAt(colours, index)}
              strokeWidth={2}
              fill={colourAt(colours, index)}
              fillOpacity={0.12}
              activeDot={activeDot}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      );
    }
    const stacked = kind === 'stacked_bar';
    return (
      <BarChart data={visual.rows} margin={CHART_MARGIN} barGap={2}>
        {axes}
        <Tooltip
          formatter={formatNumber}
          labelFormatter={formatX}
          cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
          {...tooltip}
        />
        {legend}
        {series.map((entry, index) => (
          <Bar
            key={entry.field}
            dataKey={entry.field}
            name={entry.label}
            fill={colourAt(colours, index)}
            maxBarSize={24}
            stackId={stacked ? 'stack' : undefined}
            stroke={stacked ? surface : undefined}
            strokeWidth={stacked ? 2 : 0}
            radius={!stacked || index === series.length - 1 ? [4, 4, 0, 0] : 0}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    );
  };

  return (
    <ChartFrame role="img" aria-label={chartLabel(kind, title, series, x)}>
      <ResponsiveContainer
        width="100%"
        height={compact ? COMPACT_CHART_HEIGHT : CHART_HEIGHT}
      >
        {renderChart()}
      </ResponsiveContainer>
    </ChartFrame>
  );
};

export const AlphaVisual = ({
  visual,
  compact = false,
}: AlphaVisualProps): JSX.Element => {
  const titleId = useId();
  const [asTable, setAsTable] = useState(false);
  const isChart = visual.kind === 'chart';
  const title = (visual.title ?? '').trim() || undefined;
  const columns = useMemo(() => columnsFor(visual), [visual]);

  return (
    <Root
      compact={compact}
      role="group"
      aria-labelledby={title === undefined ? undefined : titleId}
      aria-label={title === undefined ? 'Alpha visual' : undefined}
    >
      {title !== undefined || isChart ? (
        <Header>
          {title === undefined ? null : <Title id={titleId}>{title}</Title>}
          {isChart ? (
            <ToggleButton
              disableRipple
              startIcon={
                asTable ? <BarChartOutlined /> : <TableChartOutlined />
              }
              onClick={() => {
                setAsTable(current => !current);
              }}
            >
              {asTable ? 'Show as chart' : 'Show as table'}
            </ToggleButton>
          ) : null}
        </Header>
      ) : null}
      {visual.truncated ? (
        <Note>{`Showing the first ${visual.rows.length} rows`}</Note>
      ) : null}
      {isChart && !asTable ? (
        <VisualChart visual={visual} title={title} compact={compact} />
      ) : (
        <VisualTable
          title={title}
          columns={columns}
          rows={visual.rows}
          truncated={visual.truncated === true}
          compact={compact}
        />
      )}
    </Root>
  );
};
